import React from 'react';
import type { IUmpireRequest, IScorerRequest } from '../types/request';

const currencySymbol: Record<string, string> = {
  INR: '₹',
  USD: '$',
  EUR: '€',
  GBP: '£',
};

const getInitials = (name?: string): string => {
  if (!name) return '-';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }
  return name.trim().charAt(0).toUpperCase() || '-';
};

type StaffRequest = IUmpireRequest | IScorerRequest;

interface StaffRequestsListViewProps {
  requests: StaffRequest[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  getRequestId: (r: StaffRequest) => string;
  columnHeader: 'UMPIRE' | 'SCORER';
}

export const StaffRequestsListView: React.FC<StaffRequestsListViewProps> = ({
  requests,
  selectedIds,
  onToggleSelect,
  getRequestId,
  columnHeader,
}) => {
  return (
    <div className="staff-requests-list-wrap">
      <table className="staff-requests-list-table">
        <thead>
          <tr>
            <th className="staff-requests-list-th-checkbox" />
            <th>{columnHeader}</th>
            <th>TEAM ID</th>
            <th>PRICE (₹)/HR</th>
            <th>ACTION</th>
          </tr>
        </thead>
        <tbody>
          {requests.map((req) => {
            const id = getRequestId(req);
            const name = req.name ?? '-';
            const staffId =
              (req as IUmpireRequest).umpireId ??
              (req as IScorerRequest).scorerId ??
              '';
            const fees = req.fees ?? 0;
            const currency = req.currency ?? 'INR';
            const symbol = currencySymbol[currency] ?? currency;
            const priceText = `${symbol}${fees.toFixed(0)}`;

            const imageUri = req.imageUri;
            const cdnBaseUrl = import.meta.env.VITE_IMAGE_CDN_BASE_URL;
            const avatarSrc =
              imageUri && cdnBaseUrl
                ? `${cdnBaseUrl.replace(/\/$/, '')}/${String(imageUri).replace(/^\//, '')}`
                : null;
            const initials = getInitials(name);

            return (
              <tr
                key={id}
                className={selectedIds.has(id) ? 'staff-requests-list-row-selected' : ''}
              >
                <td className="staff-requests-list-td-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(id)}
                    onChange={() => onToggleSelect(id)}
                    aria-label={`Select ${name}`}
                  />
                </td>
                <td>
                  <div className="staff-requests-list-staff-cell">
                    <div className="staff-requests-list-avatar">
                      {avatarSrc ? (
                        <img src={avatarSrc} alt="" />
                      ) : (
                        <span className="staff-requests-list-avatar-initials">
                          {initials}
                        </span>
                      )}
                    </div>
                    <span className="staff-requests-list-name">{name}</span>
                  </div>
                </td>
                <td>
                  <span className="staff-requests-list-team-id">{staffId || '-'}</span>
                </td>
                <td>
                  <span className="staff-requests-list-price">{priceText}</span>
                </td>
                <td>
                  <button type="button" className="staff-requests-list-view-profile">
                    View Profile
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
